import React, { useMemo, useState } from 'react';
import { Container, Card, Table, Button, Alert, Badge, Spinner, Form, InputGroup } from 'react-bootstrap';
import { useGetNonClientsQuery, useAddNonClientMutation, useDeleteNonClientMutation } from '../app/api';

// Super Admin -> Non-Clients.
//
// The Meetings page reads an attendee as a client unless we can show otherwise.
// Two things answer that, and only one of them lives here:
//
//   * Colleagues — including everyone still reachable on a pre-merger address —
//     come from the company directory automatically (final_email + prior_email +
//     aliases). Nothing to maintain, so nothing to add on this page.
//   * Everyone else the directory will never hold — custodians, attorneys, CPAs,
//     recruiters, tool senders — is this list.
//
// Which is why the page leads with what it does NOT need: the most likely wrong
// use of it is somebody typing in 160 colleagues that already work.

const brand = { deepBlue: '#0a2947', tealBlue: '#0069a6', charcoal: '#58585b' };

function when(ts) {
  if (!ts) return '—';
  const d = new Date(ts.endsWith?.('Z') ? ts : `${ts}Z`);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleDateString();
}

export default function NonClients() {
  const { data, isLoading, refetch } = useGetNonClientsQuery();
  const [addEntry, { isLoading: adding }] = useAddNonClientMutation();
  const [removeEntry] = useDeleteNonClientMutation();

  const [value, setValue] = useState('');
  const [kind, setKind] = useState('address');
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const entries = useMemo(() => {
    const all = data?.entries || [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(e => [e.value, e.label, e.reason]
      .some(f => (f || '').toLowerCase().includes(q)));
  }, [data, filter]);

  const domainCount = (data?.entries || []).filter(e => e.kind === 'domain').length;

  const add = async () => {
    setError('');
    setNote('');
    try {
      const res = await addEntry({ value, kind, label, reason }).unwrap();
      setNote(`${res.value} will no longer be read as a client.`);
      setValue('');
      setLabel('');
      setReason('');
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not add that entry.');
    }
  };

  const drop = async (entry) => {
    setError('');
    setNote('');
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${entry.value}? It will be treated as a client again.`)) return;
    try {
      await removeEntry({ id: entry.id }).unwrap();
      setNote(`${entry.value} removed.`);
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not remove that entry.');
    }
  };

  if (isLoading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  return (
    <Container className="py-4">
      <h3 className="mb-1">Non-Clients</h3>
      <p className="text-muted" style={{ maxWidth: '48rem' }}>
        Addresses and domains that should never be read as a client. The Meetings
        page uses this when it labels attendees and decides whose email history to
        read for a prep brief.
      </p>

      <Alert variant="light" className="border" style={{ maxWidth: '48rem', fontSize: '0.88rem' }}>
        <strong>You don’t need to add colleagues here.</strong> Everyone in the
        company directory is already recognised on every address we hold for them,
        including their pre-merger one — J.L. Smith, One Team, Hammer, FSC Wealth,
        Alison and C2P are all covered. Add the people the directory will never
        contain: custodians, attorneys, CPAs, recruiters, centres of influence.
      </Alert>

      <Card className="mb-3">
        <Card.Header style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase',
                              letterSpacing: '0.5px', color: brand.deepBlue }}>
          Add an entry
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap align-items-end">
            <div style={{ flex: '0 0 9rem' }}>
              <Form.Label style={{ fontSize: '0.78rem' }} className="text-muted mb-1">Type</Form.Label>
              <Form.Select size="sm" value={kind} onChange={e => setKind(e.target.value)}>
                <option value="address">One address</option>
                <option value="domain">Whole domain</option>
              </Form.Select>
            </div>
            <div style={{ flex: '1 1 18rem' }}>
              <Form.Label style={{ fontSize: '0.78rem' }} className="text-muted mb-1">
                {kind === 'domain' ? 'Domain' : 'Email address'}
              </Form.Label>
              <Form.Control
                size="sm"
                value={value}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && value.trim()) add(); }}
                placeholder={kind === 'domain' ? 'schwab.com' : 'jane@somecpafirm.com'}
              />
            </div>
            <div style={{ flex: '1 1 12rem' }}>
              <Form.Label style={{ fontSize: '0.78rem' }} className="text-muted mb-1">Who they are</Form.Label>
              <Form.Control size="sm" value={label} onChange={e => setLabel(e.target.value)}
                            placeholder="Schwab" />
            </div>
            <div style={{ flex: '1 1 14rem' }}>
              <Form.Label style={{ fontSize: '0.78rem' }} className="text-muted mb-1">Why (optional)</Form.Label>
              <Form.Control size="sm" value={reason} onChange={e => setReason(e.target.value)}
                            placeholder="Custodian, not a client" />
            </div>
            <Button size="sm" onClick={add} disabled={adding || !value.trim()}>
              {adding ? <Spinner size="sm" animation="border" /> : 'Add'}
            </Button>
          </div>
          {kind === 'domain' && (
            <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
              A domain covers everyone there — use it for firms whose individual
              addresses you can’t list, not for anywhere a real client might sit.
            </div>
          )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" className="py-2" style={{ fontSize: '0.88rem' }}>{error}</Alert>}
      {note && <Alert variant="success" className="py-2" style={{ fontSize: '0.88rem' }}>{note}</Alert>}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
          <span style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase',
                         letterSpacing: '0.5px', color: brand.deepBlue }}>
            On the list
            <span className="text-muted ms-2" style={{ fontWeight: 400, textTransform: 'none',
                                                       letterSpacing: 0 }}>
              {(data?.entries || []).length} entr{(data?.entries || []).length === 1 ? 'y' : 'ies'}
              {domainCount > 0 && ` · ${domainCount} domain${domainCount === 1 ? '' : 's'}`}
            </span>
          </span>
          <InputGroup size="sm" style={{ width: 'auto' }}>
            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
            <Form.Control value={filter} onChange={e => setFilter(e.target.value)}
                          placeholder="Filter" style={{ width: '12rem' }} />
          </InputGroup>
        </Card.Header>
        {entries.length === 0 ? (
          <Card.Body className="text-muted text-center py-4" style={{ fontSize: '0.9rem' }}>
            {(data?.entries || []).length === 0
              ? 'Nothing listed yet — colleagues are handled by the directory, so an empty list is a normal starting point.'
              : 'Nothing matches that filter.'}
          </Card.Body>
        ) : (
          <Table hover responsive className="mb-0" style={{ fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px',
                           color: brand.charcoal }}>
                <th>Address / domain</th>
                <th>Who</th>
                <th>Why</th>
                <th>Added</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td>
                    <span style={{ wordBreak: 'break-all' }}>{e.value}</span>
                    {e.kind === 'domain' && (
                      <Badge bg="" className="ms-2" style={{ background: brand.tealBlue, fontSize: '0.62rem' }}>
                        everyone here
                      </Badge>
                    )}
                  </td>
                  <td>{e.label || <span className="text-muted">—</span>}</td>
                  <td className="text-muted">{e.reason || '—'}</td>
                  <td className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {when(e.created)}
                    {e.added_by_email && <div style={{ fontSize: '0.72rem' }}>{e.added_by_email}</div>}
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-danger" onClick={() => drop(e)}
                            title="Remove from the list">
                      <i className="bi bi-x-lg" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Container>
  );
}
